import React, { useEffect, useState } from 'react';
import {
  getLocationContentApprovalSettings,
  updateLocationContentApprovalSettings,
  useQuery,
} from 'wasp/client/operations';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Button } from '../../components/ui/button';
import { toast } from '../../hooks/use-toast';
import { Shield } from 'lucide-react';

const ROLE_OPTIONS = [
  { id: 'ADMIN', label: 'Location admin' },
  { id: 'MANAGER', label: 'Manager' },
  { id: 'MEMBER', label: 'Member' },
  { id: 'SUBSCRIBER', label: 'Subscriber' },
] as const;

type MemberRoleKey = (typeof ROLE_OPTIONS)[number]['id'];

type ApprovalSettings = {
  enabled: boolean;
  autoApproveRoles: MemberRoleKey[];
  autoApprovePlatformAdmins: boolean;
  allowTrustedMemberBypass: boolean;
};

function isMemberRoleKey(value: string): value is MemberRoleKey {
  return ROLE_OPTIONS.some((r) => r.id === value);
}

type Props = {
  locationId: string;
};

export default function LocationContentApprovalSettingsCard({ locationId }: Props) {
  const { data, isLoading, refetch } = useQuery(getLocationContentApprovalSettings, {
    locationId,
  });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<ApprovalSettings | null>(null);

  useEffect(() => {
    if (data) {
      const loaded = data as ApprovalSettings;
      setForm({
        ...loaded,
        autoApproveRoles: loaded.autoApproveRoles.filter(isMemberRoleKey),
      });
    }
  }, [data]);

  const toggleRole = (roleId: MemberRoleKey, checked: boolean) => {
    if (!form) return;
    const roles = new Set(form.autoApproveRoles);
    if (checked) {
      roles.add(roleId);
    } else {
      roles.delete(roleId);
    }
    setForm({ ...form, autoApproveRoles: [...roles] });
  };

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      await updateLocationContentApprovalSettings({
        locationId,
        settings: form,
      });
      await refetch();
      toast({ title: 'Saved', description: 'Content approval settings updated.' });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save settings';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || !form) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-muted-foreground">
          Loading approval settings…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Shield className="h-5 w-5" />
          Content approval
        </CardTitle>
        <CardDescription>
          Control who can publish notices and other content immediately versus who must go through
          the approval queue. Per-member exceptions are configured on the Members tab.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label>Require approval for new posts</Label>
            <p className="text-sm text-muted-foreground">
              When on, regular members and subscribers need moderator approval unless exempted
              below.
            </p>
          </div>
          <Switch
            checked={form.enabled}
            onCheckedChange={(checked) => setForm({ ...form, enabled: checked })}
          />
        </div>

        <div className="space-y-2">
          <Label>Publish immediately — location roles</Label>
          <p className="text-sm text-muted-foreground">
            Users with these roles at this location skip the approval queue.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {ROLE_OPTIONS.map((role) => (
              <label
                key={role.id}
                className="flex items-center gap-2 rounded-md border border-border px-3 py-2"
              >
                <Checkbox
                  checked={form.autoApproveRoles.includes(role.id)}
                  disabled={!form.enabled}
                  onCheckedChange={(checked) => toggleRole(role.id, checked === true)}
                />
                <span className="text-sm">{role.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label>Platform administrators publish immediately</Label>
            <p className="text-sm text-muted-foreground">
              Users with platform Administrator or Platform Owner role.
            </p>
          </div>
          <Switch
            checked={form.autoApprovePlatformAdmins}
            disabled={!form.enabled}
            onCheckedChange={(checked) =>
              setForm({ ...form, autoApprovePlatformAdmins: checked })
            }
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label>Trusted member score bypass</Label>
            <p className="text-sm text-muted-foreground">
              Allow members with a high approval score in their primary location to post without
              review. Off by default.
            </p>
          </div>
          <Switch
            checked={form.allowTrustedMemberBypass}
            disabled={!form.enabled}
            onCheckedChange={(checked) =>
              setForm({ ...form, allowTrustedMemberBypass: checked })
            }
          />
        </div>

        <Button type="button" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save approval settings'}
        </Button>
      </CardContent>
    </Card>
  );
}
